// tslint:disable: jsdoc-format
/**
 * {
 id: 'any',
 type: 'SearchBoxField',
 label: translate(registerTranslationKey('Search for Dealer')),
 defaultValue: 'null',
 selectall:true,
 restrictedSearch: true,
 restrictedSearchDefault: [{
 value: 'all',
 label: 'All'
 }]
 }
 */
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { UntypedFormControl, ReactiveFormsModule } from '@angular/forms';
import { Register } from '@app/utils/type-registry';
import { uniqBy, merge, isEqual } from 'lodash-es';
import { combineLatest, of } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  pairwise,
  startWith,
  switchMap,
  takeUntil,
  filter,
} from 'rxjs/operators';
import { BaseFormControlWidgetComponent } from '../base-form-control-widget.component';
import {
  FormFieldOption,
  FormFieldOutputModel,
  LocalActionModel,
  Resolvable,
  Resolved,
  SearchBoxFieldInput,
} from '@trackback/widgets';
import { ParsePipe } from '@app/pipes/parse.pipe';
import { AsyncPipe } from '@angular/common';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import {
  MatOptionModule,
  MatPseudoCheckbox,
  MatRipple,
} from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';

const SYMBOL_PREVIOUS_VALUE = Symbol('PREVIOUS_VALUE');

/**
 * @title Option groups autocomplete
 */
@Component({
  selector: 'tb-search-box-field',
  templateUrl: './search-box-field.component.html',
  styleUrls: ['./search-box-field.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush, // Widget should be optimised for performance
  encapsulation: ViewEncapsulation.None,
  imports: [
    MatFormFieldModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatOptionModule,
    NgxMatSelectSearchModule,
    AsyncPipe,
    ParsePipe,
    MatPseudoCheckbox,
    MatRipple,
  ],
})
@Register('SearchBoxField')
export class SearchBoxFieldComponent
  extends BaseFormControlWidgetComponent<
    SearchBoxFieldInput,
    FormFieldOutputModel
  >
  implements OnInit
{
  protected externalToInternal(next: unknown) {
    if (this.input?.multiple) {
      const isNullish = next === null || next === undefined;
      const isAllToken = next === 'all';
      if (isNullish || isAllToken) {
        const optionKeys = this.getOptionsKeyValues();
        if (this.input.allSelectedAsNull || isAllToken) {
          const options = this.allOptions?.() || [];
          return options.map(option => option[optionKeys.valueKey]);
        }
        return [];
      }
    }
    return next;
  }
  /** control for the MatSelect filter keyword
   * this is used to handel the search box localy which should not be updated to global state
   */
  public filterCtrl: UntypedFormControl = new UntypedFormControl();
  public _formControl: UntypedFormControl = new UntypedFormControl();

  public filteredOptions$ = this.input$.pipe(
    switchMap(input =>
      this.parse([
        input?.options ?? [],
        input?.restrictedSearchDefault,
        input?.defaultValue,
        input?.restrictedSearch,
      ] as const)
    ),
    switchMap(
      ([
        parsedOptions,
        parsedRestrictedSearchDefault,
        parsedDefaultValue,
        parsedRestrictedSearch,
      ]) => {
        const { optionsLabelKey, optionsValueKey } = this.input || {};

        const labelKey = optionsLabelKey || 'label';
        const valueKey = optionsValueKey || 'value';
        const defaultOptions: Record<string, unknown>[] = [];
        if (parsedRestrictedSearch && parsedRestrictedSearchDefault) {
          defaultOptions.push(...parsedRestrictedSearchDefault);
        }
        if (typeof parsedDefaultValue !== 'undefined' && parsedOptions) {
          const defaultOption = parsedOptions.find(
            e => e[valueKey] === Number(parsedDefaultValue)
          );
          if (defaultOption) {
            defaultOptions.push(defaultOption);
          }
        }

        const filterValue = this.filterCtrl.valueChanges.pipe(
          debounceTime(400),
          startWith(''),
          distinctUntilChanged()
        );
        const parsedOptionsWithParsedLabels = this.parse(
          parsedOptions?.map(
            (opt: FormFieldOption) => opt[labelKey] as Resolvable<string>
          ) ?? []
        ).pipe(
          map(parsedLabels => {
            return merge(
              (parsedOptions as FormFieldOption[]) ?? [],
              parsedLabels.map(parsedLabel => ({ [labelKey]: parsedLabel }))
            ) as unknown as Resolved<FormFieldOption>[];
          })
        );
        return combineLatest([
          filterValue,
          parsedOptionsWithParsedLabels,
        ] as const).pipe(
          map(([searchValue, fullyParsedOptions]) => {
            let result: Record<string, unknown>[];
            if (!searchValue) {
              // setting value when search is empty
              if (parsedRestrictedSearch) {
                // return the value of old search
                return SYMBOL_PREVIOUS_VALUE;
              } else {
                result = fullyParsedOptions;
              }
            } else {
              searchValue = searchValue.toLowerCase();
              result = fullyParsedOptions.filter(
                option =>
                  option[labelKey].toLowerCase().indexOf(searchValue) > -1
              );
              const value = this._formControl.value;
              // to avoid unselecting select box when no option is selcted.
              if (value && parsedRestrictedSearch) {
                const formValueOption = fullyParsedOptions.find(
                  option => option[valueKey] === value
                );

                if (formValueOption) {
                  if (result.length) {
                    result = [...result, formValueOption];
                  } else {
                    result = [formValueOption];
                  }
                }
              }
            }
            return uniqBy(defaultOptions.concat(result), opt => opt[valueKey]);
          }),
          startWith(defaultOptions),
          distinctUntilChanged(),
          pairwise(),
          map(([previous, current]) =>
            current === SYMBOL_PREVIOUS_VALUE
              ? (previous as FormFieldOption[])
              : current
          )
        );
      }
    )
  );

  public readonly filteredOptions = toSignal(this.filteredOptions$, {
    initialValue: [] as FormFieldOption[],
  });

  public readonly allOptions$ = this.input$.pipe(
    switchMap(input => this.parse(input?.options ?? [])),
    map(options =>
      Array.isArray(options) ? options.filter(option => !!option) : []
    )
  );
  public readonly allOptions = toSignal(this.allOptions$, {
    initialValue: [] as FormFieldOption[],
  });

  public readonly allChecked$ = combineLatest([
    this.allOptions$,
    this.value$,
    this.input$,
  ]).pipe(
    map(([options, value, input]) => {
      if (value === null && input.multiple && input.allSelectedAsNull) {
        return true;
      }
      if (Array.isArray(value)) {
        return options.length === value.length;
      }
      return false;
    })
  );
  public readonly allChecked = toSignal(this.allChecked$);

  public readonly filteredAllChecked$ = combineLatest([
    this.filteredOptions$,
    this.value$,
    this.input$,
  ]).pipe(
    map(([filteredOptions, value, input]) => {
      if (value === null && input.multiple && input.allSelectedAsNull) {
        return true;
      }
      if (Array.isArray(value)) {
        const optionKeys = this.getOptionsKeyValues();
        const selectedValues = new Set(value as unknown[]);
        return filteredOptions.every(option =>
          selectedValues.has(option[optionKeys.valueKey])
        );
      }
      return false;
    })
  );
  public readonly filteredAllChecked = toSignal(this.filteredAllChecked$);

  public readonly noneSelected$ = combineLatest([
    this.value$.pipe(filter(value => value !== undefined)),
    this.input$,
  ]).pipe(
    map(
      ([value, input]) =>
        !!input?.multiple &&
        (value == null || (Array.isArray(value) && value.length === 0))
    )
  );
  public readonly noneSelected = toSignal(this.noneSelected$, {
    initialValue: false,
  });

  toggleAllSelection() {
    if (!this.filteredAllChecked()) {
      const optionKeys = this.getOptionsKeyValues();
      this._formControl.setValue(
        this.filteredOptions().map(option => option[optionKeys.valueKey])
      );
    } else {
      this._formControl.setValue([]);
    }
    this._cd.detectChanges();
  }

  /**
   * Gets the options object keys for the label and the value. Allowing the consumer
   * to change the values at creation time, e.g not enforcing the keys to be label
   * and value.
   */
  private getOptionsKeyValues(): { labelKey: string; valueKey: string } {
    return {
      labelKey:
        this.input.optionsLabelKey != null
          ? this.input.optionsLabelKey
          : 'label',
      valueKey:
        this.input.optionsValueKey != null
          ? this.input.optionsValueKey
          : 'value',
    };
  }

  internalToExternal(next?: unknown) {
    if (this.input.multiple && this.input.allSelectedAsNull) {
      if (this._formControl.value?.length === this.allOptions().length) {
        return null;
      }
    }
    return next;
  }

  async ngOnInit() {
    await super.ngOnInit();

    const optionKeys = this.getOptionsKeyValues();

    if (this.input.multiple) {
      this.allChecked$
        .pipe(takeUntil(this.destroyed$))
        .subscribe(newValue =>
          this.updateOutput({ allSelected: newValue } as any)
        );
    }

    if (this.input.defaultValue) {
      this.filteredOptions$
        .pipe(
          switchMap(() => this.parse(this.input.defaultValue)),
          distinctUntilChanged(isEqual)
        )
        .subscribe(parsedDefaultValue => {
          if (this.input.multiple && parsedDefaultValue === 'all') {
            this._formControl.patchValue(
              this.allOptions().map(option => option[optionKeys.valueKey])
            );
          }
        });
    }
  }

  handleResetAction(action: LocalActionModel) {
    if (this.input.multiple && !this.allChecked()) {
      const optionKeys = this.getOptionsKeyValues();
      this._formControl.setValue(
        this.filteredOptions().map(option => option[optionKeys.valueKey])
      );
      this._cd.detectChanges();
    } else {
      super.handleResetAction(action);
    }
    return of(null);
  }

  handleSetValueAction(action: LocalActionModel) {
    if (action.payload === 'all' && this.input.multiple) {
      const optionKeys = this.getOptionsKeyValues();
      this._formControl.patchValue(
        this.filteredOptions().map(option => option[optionKeys.valueKey])
      );
      this._cd.detectChanges();
    } else {
      this._value = action.payload;
    }
    return of(null);
  }
}
